<template>
  <div
    class="pixso-page industrial-solution"
    :class="{ 'industrial-solution--en': isEnglish }"
  >
    <section
      class="industrial-solution__hero pixso-bleed"
      aria-labelledby="industrial-hero-title"
    >
      <video
        class="industrial-solution__hero-video"
        autoplay
        muted
        loop
        playsinline
        aria-hidden="true"
      >
        <source :src="heroVideo" type="video/mp4" />
      </video>
      <div class="industrial-solution__hero-copy">
        <h1 id="industrial-hero-title" class="industrial-solution__hero-title">
          {{ t('IndustrialInspection.hero.title') }}
        </h1>
        <p class="industrial-solution__hero-kicker">{{ t('IndustrialInspection.hero.kicker') }}</p>
      </div>
    </section>

    <section class="industrial-solution__section industrial-solution__pain-section">
      <div class="pixso-container industrial-solution__section-head">
        <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.pain.eyebrow') }}</p>
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span class="industrial-solution__title-accent">{{ t('IndustrialInspection.sections.pain.titleAccent') }}</span>
          <span>{{ t('IndustrialInspection.sections.pain.titleLine1') }}</span>
          <span>{{ t('IndustrialInspection.sections.pain.titleLine2') }}</span>
        </h2>
      </div>

      <div class="pixso-container industrial-solution__pain-gallery">
        <AccordionImageGallery
          :items="painGalleryItems"
          :aria-label="t('IndustrialInspection.sections.pain.eyebrow')"
        />
      </div>
    </section>

    <section class="industrial-solution__how pixso-bleed" :style="howStyle">
      <div class="pixso-stage industrial-solution__how-stage">
        <div class="pixso-container industrial-solution__section-head">
          <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.how.eyebrow') }}</p>
          <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
            <span class="industrial-solution__title-accent">{{ t('IndustrialInspection.sections.how.titleAccent') }}</span>
            <span>{{ t('IndustrialInspection.sections.how.title') }}</span>
          </h2>
        </div>
      </div>
    </section>

    <section class="industrial-solution__section industrial-solution__features-section">
      <div class="pixso-container industrial-solution__section-head">
        <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.features.eyebrow') }}</p>
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span class="industrial-solution__title-accent">{{ t('IndustrialInspection.sections.features.titleAccent') }}</span>
          <span>{{ t('IndustrialInspection.sections.features.title') }}</span>
        </h2>
      </div>

      <FeatureMosaicGrid
        class="industrial-solution__wide industrial-solution__feature-showcase"
        layout="industrial"
        :items="featureMosaicItems"
        :aria-label="t('IndustrialInspection.sections.features.title')"
        gap="var(--industrial-gap-md)"
        card-radius="var(--industrial-radius-card)"
        compact-card-radius="var(--industrial-radius-compact)"
        bottom-height="514px"
      />
    </section>

    <section class="industrial-solution__section industrial-solution__hardware-section">
      <div class="pixso-container industrial-solution__section-head industrial-solution__section-head--hardware">
        <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.hardware.eyebrow') }}</p>
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('IndustrialInspection.sections.hardware.title') }}</span>
        </h2>
        <p class="industrial-solution__section-desc industrial-solution__section-desc--medium">
          {{ t('IndustrialInspection.sections.hardware.description') }}
        </p>
        <PixsoButton class="industrial-solution__cta" :text="t('IndustrialInspection.sections.hardware.cta')" to="/about/contact" />
      </div>

      <HardwareCalloutScroller
        class="industrial-solution__hardware-stage"
        :image-src="hardwareImage"
        :image-alt="t('IndustrialInspection.alt.hardwareImage')"
        :aria-label="t('IndustrialInspection.alt.hardwareImage')"
        :items="hardwareItems"
        :image-layout="hardwareImageLayout"
        stage-aspect-ratio="1627 / 1110"
        :stage-ratio="1627 / 799"
        viewport-fit
        viewport-height="calc(100vh - var(--pixso-nav-height) - 32px)"
        viewport-inset="clamp(72px, 7vw, 160px)"
        :scroll-step="430"
        mobile-image-width="var(--industrial-hardware-mobile-image-width)"
        :scroll-hint-src="scrollHintIcon"
        scroll-hint-placement="viewport"
        scroll-hint-bottom="6vh"
      />
    </section>

    <section class="industrial-solution__section industrial-solution__control-section">
      <div class="pixso-container industrial-solution__section-head">
        <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.control.eyebrow') }}</p>
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('IndustrialInspection.sections.control.title') }}</span>
        </h2>
        <p class="industrial-solution__section-desc industrial-solution__section-desc--wide">
          {{ t('IndustrialInspection.sections.control.description') }}
        </p>
      </div>

      <div class="pixso-container">
        <TabbedVideoPlayer
          :items="capabilityVideos"
          :poster="controlPosterImage"
          :caption="t('IndustrialInspection.sections.control.note')"
          :tablist-label="t('IndustrialInspection.sections.control.tablistAria')"
        />
      </div>
    </section>

    <section class="industrial-solution__section industrial-solution__platform-section">
      <div class="pixso-container industrial-solution__section-head industrial-solution__section-head--narrow">
        <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.platform.eyebrow') }}</p>
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('IndustrialInspection.sections.platform.titleAccent') }}</span>
          <span>{{ t('IndustrialInspection.sections.platform.title') }}</span>
        </h2>
        <p class="industrial-solution__section-desc industrial-solution__section-desc--wide" data-text-reveal="inline">
          <span>{{ t('IndustrialInspection.sections.platform.descriptionPrefix') }}</span>
          <span class="industrial-solution__desc-accent">{{ t('IndustrialInspection.sections.platform.descriptionHighlight') }}</span>
          <span>{{ t('IndustrialInspection.sections.platform.descriptionSuffix') }}</span>
        </p>
      </div>

      <div class="industrial-solution__wide industrial-solution__platform-shell">
        <PlatformGrid
          :items="platformCards"
          :aria-label="t('IndustrialInspection.sections.platform.title')"
        />
      </div>
    </section>

    <section class="industrial-solution__section industrial-solution__sensors-section">
      <div class="pixso-container industrial-solution__section-head">
        <p class="pixso-eyebrow">{{ t('IndustrialInspection.sections.sensors.eyebrow') }}</p>
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span class="industrial-solution__title-accent">{{ t('IndustrialInspection.sections.sensors.titleAccent') }}</span>
          <span>{{ t('IndustrialInspection.sections.sensors.title') }}</span>
        </h2>
      </div>

      <div class="pixso-container industrial-solution__sensor-layout">
        <SensorAccordionPanel
          :items="sensorItems"
          :aria-label="t('IndustrialInspection.sections.sensors.eyebrow')"
          :media-label="t('IndustrialInspection.alt.sensorImage')"
        />
      </div>
    </section>

    <section class="industrial-solution__section industrial-solution__scenario-section industrial-solution__section--last">
      <div class="industrial-solution__wide industrial-solution__section-head">
        <h2 class="industrial-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('IndustrialInspection.sections.scenario.title') }}</span>
        </h2>
      </div>

      <div class="industrial-solution__wide industrial-solution__scenario-gallery">
        <ScenarioGrid
          :items="scenarioCards"
          :aria-label="t('IndustrialInspection.sections.scenario.title')"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AccordionImageGallery from '@/components/pixso/AccordionImageGallery.vue'
import FeatureMosaicGrid from '@/components/pixso/FeatureMosaicGrid.vue'
import HardwareCalloutScroller from '@/components/pixso/HardwareCalloutScroller.vue'
import PixsoButton from '@/components/pixso/PixsoButton.vue'
import PlatformGrid from '@/components/pixso/PlatformGrid.vue'
import ScenarioGrid from '@/components/pixso/ScenarioGrid.vue'
import SensorAccordionPanel from '@/components/pixso/SensorAccordionPanel.vue'
import TabbedVideoPlayer from '@/components/pixso/TabbedVideoPlayer.vue'
import heroVideo from '@/assets/videos/pixso-industrial-solution/industrial-banner.mp4'
import painCoverageImage from '@/assets/images/pixso-industrial-solution/pain-coverage.jpg'
import painHighTempImage from '@/assets/images/pixso-industrial-solution/pain-high-temp.jpg'
import painLowEfficiencyImage from '@/assets/images/pixso-industrial-solution/pain-low-efficiency.jpg'
import painRepeatImage from '@/assets/images/pixso-industrial-solution/pain-repeat.jpg'
import howImage from '@/assets/images/pixso-industrial-solution/how-background.jpg'
import featureMainImage from '@/assets/images/pixso-industrial-solution/feature-main-clean.jpg'
import featureSceneImage from '@/assets/images/pixso-industrial-solution/feature-ai-recognition-clean.jpg'
import featureModuleImage from '@/assets/images/pixso-industrial-solution/feature-slam-clean.jpg'
import featureTraverseImage from '@/assets/images/pixso-industrial-solution/feature-temperature-clean.jpg'
import featureSpeedMediaImage from '@/assets/images/pixso-industrial-solution/feature-stairs-clean.jpg'
import hardwareImage from '@/assets/images/pixso-industrial-solution/image24.png'
import scrollHintIcon from '@/assets/images/pixso-common/xiahua.svg'
import controlPosterImage from '@/assets/images/pixso-industrial-solution/control-poster.jpg'
import platformTaskImage from '@/assets/images/pixso-industrial-solution/platform-task.jpg'
import platformControlImage from '@/assets/images/pixso-industrial-solution/platform-control.jpg'
import platformAlarmImage from '@/assets/images/pixso-industrial-solution/platform-alarm.jpg'
import platformDataImage from '@/assets/images/pixso-industrial-solution/platform-data.jpg'
import platformAlgorithmImage from '@/assets/images/pixso-industrial-solution/platform-algorithm.jpg'
import platformDeviceImage from '@/assets/images/pixso-industrial-solution/platform-device.jpg'
import sensorStandardImage from '@/assets/images/pixso-industrial-solution/sensor-standard.png'
import sensorPartialDischargeImage from '@/assets/images/pixso-industrial-solution/sensor-partial-discharge.jpg'
import sensorVibrationImage from '@/assets/images/pixso-industrial-solution/sensor-vibration.png'
import sensorManipulatorImage from '@/assets/images/pixso-industrial-solution/sensor-manipulator.jpg'
import scenarioPowerImage from '@/assets/images/pixso-industrial-solution/scenario-power.jpg'
import scenarioMineImage from '@/assets/images/pixso-industrial-solution/scenario-mine.jpg'
import scenarioWarehouseImage from '@/assets/images/pixso-industrial-solution/scenario-warehouse.jpg'
import scenarioChemicalImage from '@/assets/images/pixso-industrial-solution/scenario-chemical.jpg'
import scenarioPortImage from '@/assets/images/pixso-industrial-solution/scenario-port.jpg'
import scenarioWorkshopImage from '@/assets/images/pixso-industrial-solution/scenario-workshop.jpg'
import oneClickStartVideoZh from '@/assets/videos/pixso-industrial-solution/one-click-start.mp4'
import oneClickStartVideoEn from '@/assets/i18n/en/solution/industrial/one-click-start.mp4'
import slamMappingVideoZh from '@/assets/videos/pixso-industrial-solution/slam-mapping.mp4'
import slamMappingVideoEn from '@/assets/i18n/en/solution/industrial/slam-mapping.mp4'
import routePlanningVideoZh from '@/assets/videos/pixso-industrial-solution/route-planning.mp4'
import routePlanningVideoEn from '@/assets/i18n/en/solution/industrial/route-planning.mp4'
import stairClimbingVideoZh from '@/assets/videos/pixso-industrial-solution/stair-climbing.mp4'
import stairClimbingVideoEn from '@/assets/i18n/en/solution/industrial/stair-climbing.mp4'
import gasDetectionVideoZh from '@/assets/videos/pixso-industrial-solution/gas-detection.mp4'
import gasDetectionVideoEn from '@/assets/i18n/en/solution/industrial/gas-detection.mp4'
import hazardRecognitionVideoZh from '@/assets/videos/pixso-industrial-solution/hazard-recognition.mp4'
import hazardRecognitionVideoEn from '@/assets/i18n/en/solution/industrial/hazard-recognition.mp4'
import chargeIcon from '@/assets/images/pixso-police-solution/feature-icons/charge.svg'
import enduranceIcon from '@/assets/images/pixso-industrial-solution/icon-endurance.svg'
const howStyle = {
  backgroundImage: `url(${howImage})`
}

const { t, locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const painGalleryItems = computed(() => [
  {
    key: 'risk',
    initial: t('IndustrialInspection.painItems.risk.initial'),
    title: t('IndustrialInspection.painItems.risk.title'),
    subtitles: [
      t('IndustrialInspection.painItems.risk.point1'),
      t('IndustrialInspection.painItems.risk.point2')
    ],
    image: painHighTempImage,
    imageAlt: t('IndustrialInspection.alt.painImage')
  },
  {
    key: 'heavy',
    initial: t('IndustrialInspection.painItems.heavy.initial'),
    title: t('IndustrialInspection.painItems.heavy.title'),
    subtitles: [
      t('IndustrialInspection.painItems.heavy.point1'),
      t('IndustrialInspection.painItems.heavy.point2')
    ],
    image: painRepeatImage,
    imageAlt: t('IndustrialInspection.alt.featureMainImage')
  },
  {
    key: 'blind',
    initial: t('IndustrialInspection.painItems.blind.initial'),
    title: t('IndustrialInspection.painItems.blind.title'),
    subtitles: [
      t('IndustrialInspection.painItems.blind.point1'),
      t('IndustrialInspection.painItems.blind.point2')
    ],
    image: painCoverageImage,
    imageAlt: t('IndustrialInspection.alt.featureSceneImage')
  },
  {
    key: 'low',
    initial: t('IndustrialInspection.painItems.low.initial'),
    title: t('IndustrialInspection.painItems.low.title'),
    subtitles: [
      t('IndustrialInspection.painItems.low.point1'),
      t('IndustrialInspection.painItems.low.point2')
    ],
    image: painLowEfficiencyImage,
    imageAlt: t('IndustrialInspection.alt.featureSpeedMediaImage')
  }
])

const featureMosaicItems = computed(() => [
  {
    key: 'main',
    variant: 'hero',
    group: 'hero',
    tone: 'industrial-hero',
    image: featureMainImage,
    imageAlt: t('IndustrialInspection.alt.featureMainImage'),
    imagePosition: '62% center',
    title: t('IndustrialInspection.sections.features.main.title'),
    tags: [
      t('IndustrialInspection.sections.features.main.subtitle')
    ],
    footnote: {
      value: t('IndustrialInspection.sections.features.main.size'),
      label: t('IndustrialInspection.sections.features.main.sizeLabel')
    }
  },
  {
    key: 'slam',
    variant: 'action',
    group: 'topCards',
    tone: 'industrial-slam',
    image: featureModuleImage,
    imageAlt: t('IndustrialInspection.alt.featureModuleImage'),
    title: t('IndustrialInspection.sections.features.slam.title'),
    description: t('IndustrialInspection.sections.features.slam.description')
  },
  {
    key: 'ai-recognition',
    variant: 'image',
    group: 'topFull',
    tone: 'industrial-ai',
    image: featureSceneImage,
    imageAlt: t('IndustrialInspection.alt.featureSceneImage'),
    title: t('IndustrialInspection.sections.features.aiRecognition.title'),
    description: t('IndustrialInspection.sections.features.aiRecognition.description')
  },
  {
    key: 'semantic',
    variant: 'action',
    group: 'topCards',
    icon: 'text-scan',
    title: t('IndustrialInspection.sections.features.semantic.title'),
    description: t('IndustrialInspection.sections.features.semantic.description')
  },
  {
    key: 'sdk',
    variant: 'action',
    group: 'topCards',
    icon: 'sdk-platform',
    title: t('IndustrialInspection.sections.features.sdk.title'),
    description: t('IndustrialInspection.sections.features.sdk.description')
  },

  {
    key: 'endurance',
    variant: 'metric',
    group: 'bottomLead',
    tone: 'industrial-metric',
    iconSrc: enduranceIcon,
    metric: {
      value: t('IndustrialInspection.metrics.endurance.value'),
      label: t('IndustrialInspection.metrics.endurance.title')
    }
  },
  {
    key: 'slope',
    variant: 'metric',
    group: 'bottomLeftStack',
    tone: 'industrial-metric',
    metric: {
      value: t('IndustrialInspection.metrics.slope.value'),
      label: t('IndustrialInspection.metrics.slope.title')
    }
  },
  {
    key: 'temperature',
    variant: 'image',
    group: 'bottomLeftStack',
    tone: 'photo',
    image: featureTraverseImage,
    imageAlt: t('IndustrialInspection.alt.featureTraverseImage'),
    metric: {
      value: t('IndustrialInspection.metrics.temperature.value'),
      label: t('IndustrialInspection.metrics.temperature.title')
    }
  },
  {
    key: 'stairs',
    variant: 'image',
    group: 'bottomRightStack',
    tone: 'photo',
    image: featureSpeedMediaImage,
    imageAlt: t('IndustrialInspection.alt.featureSpeedMediaImage'),
    metric: {
      value: t('IndustrialInspection.metrics.stairs.value'),
      label: t('IndustrialInspection.metrics.stairs.title')
    }
  },
  {
    key: 'speed',
    variant: 'metric',
    group: 'bottomRightStack',
    tone: 'industrial-metric',
    metric: {
      value: t('IndustrialInspection.metrics.speed.value'),
      label: t('IndustrialInspection.metrics.speed.title')
    }
  },
  {
    key: 'charge',
    variant: 'metric',
    group: 'bottomTrail',
    tone: 'industrial-metric',
    iconSrc: chargeIcon,
    metric: {
      value: t('IndustrialInspection.metrics.charge.value'),
      label: t('IndustrialInspection.metrics.charge.title')
    }
  }
])

const hardwareStageY = (percent) => {
  const fullStageHeight = 1392
  const croppedStageTop = 282
  const croppedStageHeight = 1110

  return (((percent / 100) * fullStageHeight - croppedStageTop) / croppedStageHeight) * 100
}

const hardwareCssVar = (name) => `var(--industrial-hardware-${name})`

const hardwareImageLayout = {
  left: 50,
  top: 0,
  width: 70.19,
  height: 100,
  transform: 'translateX(-50%)'
}

const hardwareItems = computed(() => [
  {
    key: 'dual',
    title: t('IndustrialInspection.hardware.dual.title'),
    note: { x: 0, y: hardwareCssVar('note-dual-y'), width: hardwareCssVar('note-left-width') },
    dot: { x: 38.05, y: hardwareStageY(43.68) },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 38.05, y: hardwareStageY(43.68) }
    },
    lines: [
      [{ text: t('IndustrialInspection.hardware.dual.line1') }],
      [{ text: t('IndustrialInspection.hardware.dual.line2') }],
      [{ text: t('IndustrialInspection.hardware.dual.line3') }]
    ]
  },
  {
    key: 'lidar',
    title: t('IndustrialInspection.hardware.lidar.title'),
    note: { x: 0, y: hardwareCssVar('note-lidar-y'), width: hardwareCssVar('note-left-width') },
    dot: { x: 33.5, y: hardwareStageY(55.46) },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 33.5, y: hardwareStageY(55.46) }
    },
    lines: [
      [{ text: t('IndustrialInspection.hardware.lidar.line1') }],
      [{ text: t('IndustrialInspection.hardware.lidar.line2') }],
      [{ text: t('IndustrialInspection.hardware.lidar.line3'), highlight: true }]
    ]
  },
  {
    key: 'arm',
    title: t('IndustrialInspection.hardware.arm.title'),
    side: 'right',
    note: { x: hardwareCssVar('note-right-x'), y: hardwareCssVar('note-arm-y'), width: hardwareCssVar('note-right-width') },
    dot: { x: 56.06, y: hardwareStageY(37.57) },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 56.06, y: hardwareStageY(37.57) }
    },
    lines: [
      [{ text: t('IndustrialInspection.hardware.arm.description') }]
    ]
  },
  {
    key: 'gas',
    title: t('IndustrialInspection.hardware.gas.title'),
    side: 'right',
    note: { x: hardwareCssVar('note-right-x'), y: hardwareCssVar('note-gas-y'), width: hardwareCssVar('note-right-width') },
    dot: { x: 40.75, y: hardwareStageY(56.11) },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 40.75, y: hardwareStageY(56.11) }
    },
    lines: [
      [
        { text: t('IndustrialInspection.hardware.gas.line1Prefix') },
        { text: t('IndustrialInspection.hardware.gas.line1Highlight'), highlight: true },
        { text: t('IndustrialInspection.hardware.gas.line1Suffix') }
      ],
      [{ text: t('IndustrialInspection.hardware.gas.line2') }]
    ]
  },
  {
    key: 'edge',
    title: t('IndustrialInspection.hardware.edge.title'),
    side: 'right',
    note: { x: hardwareCssVar('note-right-x'), y: hardwareCssVar('note-edge-y'), width: hardwareCssVar('note-right-width') },
    dot: { x: 50.22, y: hardwareStageY(57.76) },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 50.22, y: hardwareStageY(57.76) }
    },
    lines: [
      [{ text: t('IndustrialInspection.hardware.edge.line1') }],
      [
        { text: t('IndustrialInspection.hardware.edge.line2Prefix') },
        { text: t('IndustrialInspection.hardware.edge.line2Highlight'), highlight: true },
        { text: t('IndustrialInspection.hardware.edge.line2Suffix') }
      ],
      [
        { text: t('IndustrialInspection.hardware.edge.line3Prefix') },
        { text: t('IndustrialInspection.hardware.edge.line3Highlight'), highlight: true },
        { text: t('IndustrialInspection.hardware.edge.line3Suffix') }
      ]
    ]
  }
])

const platformCards = computed(() => [
  { key: 'task', title: t('IndustrialInspection.platformCards.task'), image: platformTaskImage, alt: t('IndustrialInspection.platformCards.task') },
  { key: 'control', title: t('IndustrialInspection.platformCards.control'), image: platformControlImage, alt: t('IndustrialInspection.platformCards.control') },
  { key: 'alarm', title: t('IndustrialInspection.platformCards.alarm'), image: platformAlarmImage, alt: t('IndustrialInspection.platformCards.alarm') },
  { key: 'data', title: t('IndustrialInspection.platformCards.data'), image: platformDataImage, alt: t('IndustrialInspection.platformCards.data') },
  { key: 'algorithm', title: t('IndustrialInspection.platformCards.algorithm'), image: platformAlgorithmImage, alt: t('IndustrialInspection.platformCards.algorithm') },
  { key: 'device', title: t('IndustrialInspection.platformCards.device'), image: platformDeviceImage, alt: t('IndustrialInspection.platformCards.device') }
])

const scenarioCards = computed(() => [
  { key: 'power', title: t('IndustrialInspection.scenarioCards.power'), description: t('IndustrialInspection.scenarioCards.powerDesc'), image: scenarioPowerImage, alt: t('IndustrialInspection.scenarioCards.power') },
  { key: 'mine', title: t('IndustrialInspection.scenarioCards.mine'), description: t('IndustrialInspection.scenarioCards.mineDesc'), image: scenarioMineImage, alt: t('IndustrialInspection.scenarioCards.mine') },
  { key: 'warehouse', title: t('IndustrialInspection.scenarioCards.warehouse'), description: t('IndustrialInspection.scenarioCards.warehouseDesc'), image: scenarioWarehouseImage, alt: t('IndustrialInspection.scenarioCards.warehouse') },
  { key: 'chemical', title: t('IndustrialInspection.scenarioCards.chemical'), description: t('IndustrialInspection.scenarioCards.chemicalDesc'), image: scenarioChemicalImage, alt: t('IndustrialInspection.scenarioCards.chemical') },
  { key: 'port', title: t('IndustrialInspection.scenarioCards.port'), description: t('IndustrialInspection.scenarioCards.portDesc'), image: scenarioPortImage, alt: t('IndustrialInspection.scenarioCards.port') },
  { key: 'workshop', title: t('IndustrialInspection.scenarioCards.workshop'), description: t('IndustrialInspection.scenarioCards.workshopDesc'), image: scenarioWorkshopImage, alt: t('IndustrialInspection.scenarioCards.workshop') }
])

const capabilityVideos = computed(() => [
  { key: 'one-click', buttonLabel: t('IndustrialInspection.capabilityTabs.oneClick'), ariaLabel: t('IndustrialInspection.alt.controlImage'), src: isEnglish.value ? oneClickStartVideoEn : oneClickStartVideoZh },
  { key: 'slam', buttonLabel: t('IndustrialInspection.capabilityTabs.slam'), ariaLabel: t('IndustrialInspection.alt.controlImage'), src: isEnglish.value ? slamMappingVideoEn : slamMappingVideoZh },
  { key: 'route', buttonLabel: t('IndustrialInspection.capabilityTabs.route'), ariaLabel: t('IndustrialInspection.alt.controlImage'), src: isEnglish.value ? routePlanningVideoEn : routePlanningVideoZh },
  { key: 'stairs', buttonLabel: t('IndustrialInspection.capabilityTabs.stairs'), ariaLabel: t('IndustrialInspection.alt.controlImage'), src: isEnglish.value ? stairClimbingVideoEn : stairClimbingVideoZh },
  { key: 'gas', buttonLabel: t('IndustrialInspection.capabilityTabs.gas'), ariaLabel: t('IndustrialInspection.alt.controlImage'), src: isEnglish.value ? gasDetectionVideoEn : gasDetectionVideoZh },
  { key: 'hazard', buttonLabel: t('IndustrialInspection.capabilityTabs.hazard'), ariaLabel: t('IndustrialInspection.alt.controlImage'), src: isEnglish.value ? hazardRecognitionVideoEn : hazardRecognitionVideoZh }
])

const sensorItems = computed(() => [
  {
    key: 'standard',
    title: t('IndustrialInspection.sensorItems.standard.title'),
    description: t('IndustrialInspection.sensorItems.standard.description'),
    image: sensorStandardImage,
    imagePosition: '32% center'
  },
  {
    key: 'partial-discharge',
    title: t('IndustrialInspection.sensorItems.partialDischarge.title'),
    description: t('IndustrialInspection.sensorItems.partialDischarge.description'),
    image: sensorPartialDischargeImage,
    imageFit: 'contain'
  },
  {
    key: 'vibration',
    title: t('IndustrialInspection.sensorItems.vibration.title'),
    description: t('IndustrialInspection.sensorItems.vibration.description'),
    image: sensorVibrationImage
  },
  {
    key: 'manipulator',
    title: t('IndustrialInspection.sensorItems.manipulator.title'),
    description: t('IndustrialInspection.sensorItems.manipulator.description'),
    image: sensorManipulatorImage
  }
])

</script>

<style scoped>
.industrial-solution {
  --industrial-color-surface: var(--pixso-color-surface);
  --industrial-color-card-end: #2a2b38;
  --industrial-color-control: #1f1f1f;
  --industrial-color-inverse-bg: var(--pixso-color-text);
  --industrial-color-inverse-text: var(--pixso-color-bg);
  --industrial-content-max: 1200px;
  --industrial-wide-max: 1792px;
  --industrial-hardware-stage-width: 1627px;
  --industrial-hardware-stage-offset: 175px;
  --industrial-section-space: var(--pixso-section-gap-xl);
  --industrial-hardware-section-top: var(--industrial-section-space);
  --industrial-hardware-note-left-width: 26.49%;
  --industrial-hardware-note-right-width: 26.49%;
  --industrial-hardware-note-right-x: 73.51%;
  --industrial-hardware-note-dual-y: 16.39%;
  --industrial-hardware-note-lidar-y: 41.54%;
  --industrial-hardware-note-arm-y: 16.39%;
  --industrial-hardware-note-gas-y: 32.97%;
  --industrial-hardware-note-edge-y: 52.26%;
  --industrial-gap-xs: var(--pixso-gap-xs);
  --industrial-gap-sm: var(--pixso-gap-sm);
  --industrial-gap-md: var(--pixso-gap-md);
  --industrial-gap-lg: var(--pixso-gap-lg);
  --industrial-radius-card: var(--pixso-radius-lg);
  --industrial-radius-compact: var(--pixso-radius-md);
  --industrial-radius-pill: calc(var(--pixso-radius-lg) - var(--pixso-gap-xs));
  --industrial-page-bottom-space: min(var(--pixso-section-gap-xl), 18.75vw);
  padding-bottom: var(--industrial-page-bottom-space);
  font-family: var(--pixso-font-cn);
}

.industrial-solution--en {
  --industrial-hardware-note-left-width: 28.5%;
  --industrial-hardware-note-right-width: 31.5%;
  --industrial-hardware-note-right-x: 68.5%;
  --industrial-hardware-note-lidar-y: 52.5%;
  --industrial-hardware-note-gas-y: 42.5%;
  --industrial-hardware-note-edge-y: 65.5%;
}

.industrial-solution__wide {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--industrial-wide-max));
  margin: 0 auto;
}

.industrial-solution__hero {
  width: 100vw;
  margin-left: calc(50% - 50vw);
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  overflow: hidden;
  background: var(--pixso-color-bg);
}

.industrial-solution__hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.24) 0%, rgba(0, 0, 0, 0.08) 52%, rgba(0, 0, 0, 0.48) 100%);
  pointer-events: none;
}

.industrial-solution__hero-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.industrial-solution__hero-copy {
  position: absolute;
  z-index: 2;
  top: clamp(96px, 12vw, 172px);
  left: 50%;
  transform: translateX(-50%);
  width: min(calc(100% - var(--pixso-page-padding)), 1920px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: var(--pixso-color-text);
  pointer-events: none;
}

.industrial-solution__hero-kicker {
  margin: 8px 0 0;
  font-weight: 700;
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.industrial-solution__hero-title {
  margin: 0;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-96);
  line-height: var(--pixso-lh-96);
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.industrial-solution__section {
  padding-top: 0;
}

.industrial-solution__section--last {
  padding-bottom: 0;
}

.industrial-solution__section-head {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--industrial-content-max));
  margin: 0 auto;
  padding: 0 var(--industrial-gap-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--industrial-gap-sm);
  text-align: center;
}

.industrial-solution__section-head--left {
  align-items: flex-start;
  text-align: left;
}

.industrial-solution__section-head--narrow {
  max-width: var(--industrial-content-max);
}

.industrial-solution__section-head.industrial-solution__section-head--hardware {
  gap: 0;
}

.industrial-solution__section-head--hardware .industrial-solution__stack-title,
.industrial-solution__section-head--hardware .industrial-solution__section-desc {
  margin-top: var(--industrial-gap-sm);
}

.industrial-solution__section-head--hardware .industrial-solution__cta {
  margin-top: var(--industrial-gap-lg, var(--pixso-gap-lg));
}

.industrial-solution__stack-title {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.industrial-solution__stack-title--left {
  align-items: flex-start;
}

.industrial-solution__stack-title > span {
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-48), 5vw, var(--pixso-fs-96));
  line-height: clamp(var(--pixso-lh-48), 6vw, var(--pixso-lh-96));
}

.industrial-solution__title-accent {
  color: var(--pixso-color-primary);
}

.industrial-solution__section-desc {
  margin: 0;
  color: var(--pixso-color-muted);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-16), 1.25vw, var(--pixso-fs-24));
  line-height: clamp(var(--pixso-lh-16), 1.77vw, var(--pixso-lh-24));
}

.industrial-solution__section-desc--medium {
  max-width: 1152px;
}

.industrial-solution__section-desc--wide {
  max-width: 1200px;
}

.industrial-solution__desc-accent {
  color: var(--pixso-color-primary);
}

.industrial-solution__media-card {
  border-radius: var(--industrial-radius-card);
  overflow: hidden;
  background: var(--industrial-color-surface);
}

.industrial-solution__media-card img {
  width: 100%;
  display: block;
  object-fit: cover;
}

.industrial-solution__how {
  position: relative;
  margin-top: var(--industrial-section-space);
  min-height: clamp(520px, 56.25vw, 1080px);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  overflow: hidden;
}

.industrial-solution__how::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.96) 0%, rgba(0, 0, 0, 0.78) 8%, rgba(0, 0, 0, 0.46) 22%, rgba(0, 0, 0, 0.14) 42%, rgba(0, 0, 0, 0.08) 58%, rgba(0, 0, 0, 0.5) 82%, rgba(0, 0, 0, 0.9) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.22) 0%, rgba(0, 0, 0, 0) 52%, rgba(0, 0, 0, 0.16) 100%);
  pointer-events: none;
}

.industrial-solution__how-stage {
  position: relative;
  z-index: 2;
  min-height: inherit;
  display: flex;
  align-items: flex-start;
}

.industrial-solution__how .industrial-solution__section-head {
  padding-top: 0;
}

.industrial-solution__pain-section {
  margin-top: var(--industrial-section-space);
}

.industrial-solution__pain-gallery {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--industrial-content-max));
  margin-top: var(--industrial-gap-lg, var(--pixso-gap-lg));
}

.industrial-solution__features-section,
.industrial-solution__control-section,
.industrial-solution__platform-section,
.industrial-solution__sensors-section,
.industrial-solution__scenario-section {
  margin-top: var(--industrial-section-space);
}

.industrial-solution__hardware-section {
  margin-top: var(--industrial-hardware-section-top);
}

.industrial-solution__feature-showcase {
  margin-top: var(--industrial-gap-lg, var(--pixso-gap-lg));
}

/* Keep action cards consistent with the other solution pages: the copy uses
 * the full card width and the icon is anchored to the lower-right corner. */
.industrial-solution__feature-showcase :deep(.feature-mosaic-card--action) {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--pixso-gap-xs);
  padding: var(--pixso-gap-md);
}

.industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__text) {
  min-width: 0;
  width: 100%;
  /* Reserve room for 48px icon (FeatureMosaicCard max). */
  padding-right: calc(48px + var(--pixso-gap-sm));
}

.industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__text p) {
  margin-top: var(--pixso-gap-xs);
  font-weight: 400;
}

.industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__icon--route) {
  position: absolute;
  right: var(--pixso-gap-md);
  bottom: var(--pixso-gap-md);
  /* Match FeatureMosaicCard: 48px desktop max; responsive clamp handled by component media queries. */
  width: 48px;
  height: 48px;
  align-self: auto;
}

.industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-slam.feature-mosaic-card--action .feature-mosaic-card__text) {
  position: relative;
  z-index: 2;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.58);
}

/* The source layout is a 1792 × 630 mosaic. Keep the top row ratio fixed so
 * the right-hand content cannot stretch the main image at narrower widths. */
.industrial-solution__feature-showcase :deep(.feature-mosaic-grid__industrial-row--top) {
  min-height: 0;
  aspect-ratio: 1792 / 630;
}

.industrial-solution__feature-showcase :deep(.feature-mosaic-grid__industrial-right) {
  min-height: 0;
  height: 100%;
}

.industrial-solution__features-section .industrial-solution__stack-title > span {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

.industrial-solution__cta {
  width: 280px;
  height: 50px;
  border-radius: var(--industrial-radius-pill);
  background: var(--industrial-color-inverse-bg);
  color: var(--industrial-color-inverse-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.industrial-solution__cta:hover {
  background: var(--pixso-color-primary);
  transform: translateY(-1px);
}

.industrial-solution__hardware-stage {
  --industrial-hardware-mobile-image-width: min(100%, clamp(360px, 56vw, 680px));
  position: relative;
  aspect-ratio: auto;
  margin: clamp(56px, 6vh, 96px) auto 0;
}

@media (max-width: 1200px) {
  .industrial-solution__hardware-stage {
    width: min(calc(100% - var(--pixso-page-padding) * 2), var(--industrial-hardware-stage-width));
    --industrial-hardware-mobile-image-width: min(100%, clamp(520px, 56vw, 680px));
    margin-top: clamp(72px, 5vw, 96px);
  }
}

@media (min-width: 1800px) and (min-height: 820px) {
  .industrial-solution--en {
    --industrial-hardware-note-lidar-y: 41.54%;
    --industrial-hardware-note-gas-y: 32.97%;
    --industrial-hardware-note-edge-y: 52.26%;
  }
}

.industrial-solution__hardware-stage :deep(.hardware-callout-scroller__note h3) {
  font-size: var(--pixso-fs-28) !important;
  line-height: var(--pixso-lh-28) !important;
}

.industrial-solution__hardware-stage :deep(.hardware-callout-scroller__note p) {
  font-size: var(--pixso-fs-14) !important;
  line-height: var(--pixso-lh-14) !important;
}

.industrial-solution--en :deep(.hardware-callout-scroller__note h3) {
  font-size: var(--pixso-fs-24) !important;
  line-height: var(--pixso-lh-24) !important;
  font-weight: 800;
}

.industrial-solution--en :deep(.hardware-callout-scroller__note p) {
  font-size: var(--pixso-fs-12) !important;
  line-height: var(--pixso-lh-12) !important;
}

.industrial-solution--en .industrial-solution__hardware-stage:not(.hardware-callout-scroller--compact) :deep(.hardware-callout-scroller__note) {
  display: flex;
  flex-direction: column;
  gap: calc(var(--pixso-gap-xs) * 0.5);
}

.industrial-solution__hardware-section > .industrial-solution__section-head {
  position: relative;
  z-index: 2;
}

.industrial-solution__platform-shell,
.industrial-solution__scenario-gallery {
  margin-top: var(--industrial-gap-lg, var(--pixso-gap-lg));
}

.industrial-solution__scenario-section > .industrial-solution__section-head {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--industrial-wide-max));
}

.industrial-solution__platform-shell {
  margin-top: var(--industrial-gap-lg, var(--pixso-gap-lg));
}

.industrial-solution__sensor-layout {
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1200px);
  margin: var(--industrial-gap-lg, var(--pixso-gap-lg)) auto 0;
}

@media (min-width: 1201px) {
  .industrial-solution__hero-copy {
    top: 120px;
  }

  .industrial-solution__hero-kicker {
    margin-top: 0;
  }
}

@media (max-width: 1200px) {
  .industrial-solution__hero-copy {
    top: 84px;
    width: min(calc(100% - 32px), 760px);
  }

  .industrial-solution__hero {
    min-height: var(--pixso-hero-min);
  }

  .industrial-solution__hero-video {
    min-height: var(--pixso-hero-min);
    object-fit: cover;
    object-position: center top;
  }

  .industrial-solution__how {
    min-height: clamp(420px, 58vw, 640px);
  }

}

@media (max-width: 1200px) {
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy) {
    top: 50%;
    left: 50%;
    right: auto;
    width: max-content;
    max-width: 82%;
    padding: 0;
    transform: translate(-50%, -50%);
    text-align: left;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-value) {
    white-space: nowrap;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy span) {
    margin-top: calc(var(--pixso-gap-xs) * 0.5);
    text-align: left;
  }
}

@media (max-width: 1200px) {
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--action) {
    gap: var(--pixso-gap-xs);
    min-height: 0;
    padding: var(--pixso-gap-sm);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__text) {
    /* Match 巡1 icon clamp max (44px) + gap. */
    padding-right: calc(44px + var(--pixso-gap-sm));
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__text h3) {
    max-width: 100%;
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    text-wrap: balance;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__text p) {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--action .feature-mosaic-card__icon--route) {
    right: var(--pixso-gap-sm);
    bottom: var(--pixso-gap-sm);
    /* Align with FeatureMosaicCard @media (max-width: 1200px) icon scale. */
    width: clamp(22px, 14cqw, 44px);
    height: clamp(22px, 14cqw, 44px);
  }

  .industrial-solution__feature-showcase
    :deep(.feature-mosaic-grid__mobile--industrial .feature-mosaic-card--group-topCards) {
    min-height: 0;
    height: auto;
    aspect-ratio: 278.666 / 283;
  }
}

@media (max-width: 768px) {
  .industrial-solution {
    --industrial-page-bottom-space: 96px;
  }

  .industrial-solution__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .industrial-solution__hero-copy {
    top: 76px;
    width: calc(100% - 32px);
  }

  .industrial-solution__hero-kicker {
    margin-top: 4px;
  }

  .industrial-solution__media-card {
    border-radius: var(--industrial-radius-compact);
  }

  .industrial-solution__how {
    min-height: clamp(360px, 76vw, 480px);
  }

  .industrial-solution__cta {
    width: 188px;
    height: 42px;
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__main-copy h3) {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__main-copy p) {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__size p) {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__size span) {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-ai .feature-mosaic-card__image-copy h3) {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__image-copy h3),
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__text h3) {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__image-copy p),
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__text p),
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-ai .feature-mosaic-card__image-copy p) {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-10);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-slam.feature-mosaic-card--action) {
    background: var(--industrial-color-surface);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-slam.feature-mosaic-card--action .feature-mosaic-card__text) {
    position: relative;
    z-index: 2;
    text-shadow: 0 2px 14px rgba(0, 0, 0, 0.58);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-slam.feature-mosaic-card--action::after) {
    display: block;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy) {
    top: 50%;
    left: 50%;
    right: auto;
    width: max-content;
    max-width: 82%;
    padding: 0;
    transform: translate(-50%, -50%);
    text-align: left;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-value) {
    white-space: nowrap;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy span) {
    margin-top: calc(var(--pixso-gap-xs) * 0.5);
    text-align: left;
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__metric-value),
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__photo-value) {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card--industrial-metric .feature-mosaic-card__metric-value) {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__metric-panel span),
  .industrial-solution__feature-showcase :deep(.feature-mosaic-card__photo-copy span) {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }
}

.industrial-solution--en .industrial-solution__feature-showcase
  :deep(.feature-mosaic-card--action .feature-mosaic-card__text h3) {
  font-size: var(--pixso-fs-18);
  line-height: var(--pixso-lh-18);
}

.industrial-solution--en .industrial-solution__feature-showcase
  :deep(.feature-mosaic-card--action .feature-mosaic-card__text p) {
  font-size: var(--pixso-fs-12);
  line-height: var(--pixso-lh-12);
}

@media (max-width: 1200px) {
  .industrial-solution--en .industrial-solution__feature-showcase
    :deep(.feature-mosaic-card--action .feature-mosaic-card__text h3) {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
    word-break: normal;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  .industrial-solution--en .industrial-solution__feature-showcase
    :deep(.feature-mosaic-card--action .feature-mosaic-card__text p) {
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
    word-break: normal;
    overflow-wrap: break-word;
  }
}

@media (max-width: 480px) {
  .industrial-solution--en .industrial-solution__feature-showcase {
    --industrial-top-card-min-height: clamp(0px, calc(1345px - 276.5vw), 180px);
    --industrial-slope-card-min-height: clamp(0px, calc(225px - 34vw), 104px);
    --industrial-speed-card-min-height: clamp(0px, calc(226px - 33vw), 108px);
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card--group-topCards) {
    width: 100%;
    max-width: 100%;
    min-height: var(--industrial-top-card-min-height);
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card--group-topCards.feature-mosaic-card--action) {
    position: relative;
    display: block;
    padding: var(--pixso-gap-sm);
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card--group-topCards .feature-mosaic-card__text) {
    position: relative;
    z-index: 2;
    max-width: 100%;
    padding-right: 0;
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card--group-topCards .feature-mosaic-card__text h3) {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card--group-topCards .feature-mosaic-card__text p) {
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card--group-topCards .feature-mosaic-card__icon--route) {
    position: absolute;
    right: var(--pixso-gap-sm);
    bottom: var(--pixso-gap-sm);
    /* Keep 巡1 responsive icon size; do not pin to 28px. */
    width: clamp(22px, 14cqw, 44px);
    height: clamp(22px, 14cqw, 44px);
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="speed"]) {
    width: 100%;
    max-width: 100%;
    min-height: var(--industrial-speed-card-min-height);
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="slope"]) {
    width: 100%;
    max-width: 100%;
    min-height: var(--industrial-slope-card-min-height);
  }

  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="endurance"]),
  .industrial-solution--en .industrial-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="charge"]) {
    width: 100%;
    max-width: 100%;
  }
}
</style>
